import { useState } from 'react';
import { Box, Button, Dialog, TextField } from '@cere/cere-design-system';
import type { Scope } from '@cef-ai/vault-sdk';
import { useVaultSession } from '@/src/state/session';

interface Props {
  open: boolean;
  scope: Scope;
  onClose: () => void;
}

export function EditScopeDialog({ open, scope, onClose }: Props) {
  const session = useVaultSession();
  const [displayName, setDisplayName] = useState(scope.displayName ?? scope.name);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      await (await session.signingHandle()).scopes.update(scope.name, { displayName });
      await session.refreshScopes();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      title={`Edit scope: ${scope.name}`}
      onClose={() => !submitting && onClose()}
      isLoading={submitting}
      customActions={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
        <TextField
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          fullWidth
        />
        {error && <Box sx={{ color: 'error.main' }}>{error}</Box>}
      </Box>
    </Dialog>
  );
}
